import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts";
import { useAdminAuth } from "../hooks";
import { PageLoader } from "../components";

/**
 * AdminGuard - Protects routes that require admin role.
 *
 * If the user is not authenticated, they are redirected to the login page.
 * If the user is authenticated but not an admin, they are redirected to the home page.
 *
 * @example
 * // In router config:
 * {
 *   element: <AdminGuard />,
 *   children: [
 *     { path: "/admin", element: <AdminDashboardPage /> }
 *   ]
 * }
 */
export function AdminGuard() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminAuth();
  const location = useLocation();

  // Show loading state while checking authentication and admin status
  if (isAuthLoading || isAdminLoading) {
    return <PageLoader />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to home if authenticated but not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and is admin, render child routes
  return <Outlet />;
}

export default AdminGuard;
